<app-navbar />

<main id="main-content">
  <section class="hero">
    <span class="hero-grid-lines" aria-hidden="true"></span>
    <div class="container hero-grid">
      <div class="hero-content">
        <span class="pill">Powered by LeadCruncher property data</span>
        <h1 class="hero-title">Verify deceased consumers. Stop identity fraud before it starts.</h1>
        <p class="hero-subtitle">
          VerifyDeceased confirms whether a consumer is deceased using fresh nationwide death data.
          Upload your list and get back date of birth, date of death, age and last known property
          address. Built for banks, lenders, card issuers and fraud teams.
        </p>

        <div class="hero-actions">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.accountRegister.url]">Create your free account</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.howItWorks.url]">See how it works</a>
        </div>

        <p class="hero-cta-note">
          No credit card required. No contract, no minimum, no setup fee. You pay
          <strong>$0.04 per confirmed match</strong> and nothing at all for records we cannot verify.
        </p>

        <ul class="hero-trust">
          <li>New records every week</li>
          <li>All 50 states covered</li>
          <li>Pay only for matches</li>
        </ul>
      </div>

      <div class="hero-visual">
        <div class="raw-card">
          <span class="raw-label">Your uploaded list</span>
          <span class="raw-line">jane doe</span>
          <span class="raw-line raw-unknown">status unknown</span>
          <span class="raw-line raw-unknown">dob unknown</span>
        </div>

        <div class="match-card">
          <div class="match-head">
            <span class="badge-success">Verified deceased</span>
            <span class="match-score">98% match</span>
          </div>
          <div class="match-fields">
            @for (field of matchFields; track field.label) {
              <div class="match-field" [class.wide]="field.label === 'Property address'">
                <span class="match-label">{{ field.label }}</span>
                <span class="match-value">{{ field.value }}</span>
              </div>
            }
          </div>
        </div>
      </div>
    </div>
  </section>

  <section class="stat-strip">
    <div class="container stat-strip-inner">
      <div class="stat">
        <span class="stat-value">Within a week</span>
        <span class="stat-label">Records added after passing</span>
      </div>
      <div class="stat">
        <span class="stat-value">Nationwide</span>
        <span class="stat-label">All 50 states and DC</span>
      </div>
      <div class="stat">
        <span class="stat-value">$0.04</span>
        <span class="stat-label">Per verified record</span>
      </div>
      <div class="stat">
        <span class="stat-value">API ready</span>
        <span class="stat-label">Real time verification</span>
      </div>
    </div>
  </section>

  <!-- Trust signals. Every item here must be a fact we can evidence on request. -->
  <section class="trust-band" aria-label="Trust and security signals">
    <div class="container">
      <ul class="trust-signals">
        @for (signal of trustSignals; track signal.label) {
          <li class="trust-signal">
            <span class="trust-signal-icon" aria-hidden="true" [innerHTML]="iconFor(signal.icon)"></span>
            <span class="trust-signal-text">
              <span class="trust-signal-label">{{ signal.label }}</span>
              <span class="trust-signal-detail">{{ signal.detail }}</span>
            </span>
          </li>
        }
      </ul>
      <p class="trust-band-note">
        Running a vendor review? Our
        <a [routerLink]="[routes.security.url]">security and compliance page</a> has the encryption,
        retention, subprocessor and incident response detail your team will ask for.
      </p>
    </div>
  </section>

  <!-- Built for. These are the teams the product is designed for, not a customer claim. -->
  <section class="section section-alt" aria-labelledby="social-proof-heading">
    <div class="container">
      <div class="proof-strip">
        <span class="proof-strip-label">Built for fraud, risk and compliance teams at</span>
        <ul class="proof-logos">
          @for (segment of customerSegments; track segment.label) {
            <li class="proof-logo">
              <span class="proof-logo-mark" aria-hidden="true"></span>
              <span class="proof-logo-text">
                <span class="proof-logo-name">{{ segment.label }}</span>
                <span class="proof-logo-use">{{ segment.useCase }}</span>
              </span>
            </li>
          }
        </ul>
      </div>

      <div class="section-head mt-64">
        <span class="eyebrow">What you can expect</span>
        <h2 class="section-title" id="social-proof-heading">Commitments we will put in writing</h2>
        <p class="section-lead">
          We would rather show you the numbers that matter than quote ourselves. Every figure below
          is part of the service, not a marketing estimate, and we will confirm each one during your
          evaluation.
        </p>
      </div>

      <div class="proof-stats">
        @for (stat of proofStats; track stat.label) {
          <div class="proof-stat">
            <span class="proof-stat-value">{{ stat.value }}</span>
            <span class="proof-stat-label">{{ stat.label }}</span>
            <span class="proof-stat-detail">{{ stat.detail }}</span>
          </div>
        }
      </div>

      <div class="text-center mt-32">
        <a class="btn btn-outline" [routerLink]="[routes.contact.url]">Run a free sample on your data</a>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="problem-grid">
        <div class="problem-copy">
          <span class="eyebrow">The problem</span>
          <h2 class="section-title">Deceased identity fraud is a multi billion dollar problem</h2>
          <p class="section-lead">
            Fraudsters use the identities of people who have passed away to open bank accounts, apply
            for credit cards, take out loans and even secure jobs. The estates and families rarely
            notice in time, and businesses absorb the loss.
          </p>
          <p class="problem-note">
            The gap exists because most death data is slow. When records take two to three months to
            surface, criminals have a wide open window. VerifyDeceased closes that window.
          </p>
        </div>
        <ul class="check-list problem-list">
          <li>New credit and card accounts opened in a deceased person's name</li>
          <li>Loan and mortgage applications using stolen deceased identities</li>
          <li>Employment and payroll fraud during hiring and onboarding</li>
          <li>Benefit, pension and entitlement payments sent to deceased recipients</li>
          <li>Ongoing collections and outreach to accounts that should be suppressed</li>
        </ul>
      </div>
    </div>
  </section>

  <section class="section section-tint">
    <div class="container">
      <div class="edge-grid">
        <div class="edge-card highlight">
          <span class="edge-tag">VerifyDeceased</span>
          <span class="edge-time">Within a week</span>
          <p>Confirmed deceased records are available days after a person passes, closing the fraud window fast.</p>
        </div>
        <div class="edge-card">
          <span class="edge-tag">Typical providers</span>
          <span class="edge-time muted">2 to 3 months</span>
          <p>Slow reporting leaves a long gap where deceased identities can be used undetected.</p>
        </div>
      </div>
      <p class="edge-line">
        We are one of the only companies in the United States that surfaces confirmed deceased
        consumer records within a week. That speed is the difference between catching fraud and
        paying for it.
      </p>
    </div>
  </section>

  <!-- Data provenance. Buyers in this market ask this before they ask about price. -->
  <section class="section" aria-labelledby="data-sources-heading">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Data provenance</span>
        <h2 class="section-title" id="data-sources-heading">Exactly where our death data comes from</h2>
        <p class="section-lead">
          No vague claims about "millions of records". These are the sources behind every confirmed
          match, and this is how a record is verified before it reaches you.
        </p>
      </div>

      <div class="grid-3">
        @for (source of dataSources; track source.title) {
          <article class="feature-card source-card">
            <span class="icon-badge" [innerHTML]="iconFor(source.icon)"></span>
            <h3>{{ source.title }}</h3>
            <p>{{ source.description }}</p>
            <span class="source-cadence">{{ source.cadence }}</span>
          </article>
        }
      </div>

      <div class="source-note">
        <span class="icon-badge accent" [innerHTML]="iconFor('shield')"></span>
        <div>
          <h3>Why we are faster than a federal file alone</h3>
          <p>
            The Death Master File is one input, and on its own it is both incomplete and slow.
            Obituaries, funeral notices and registrar filings usually surface a death well before it
            reaches any federal file. We ingest continuously and corroborate against supporting
            evidence before publishing, because a false positive on a living consumer is the
            costliest error we can make.
          </p>
          <a class="btn btn-outline" [routerLink]="[routes.howItWorks.url]">See how a record is confirmed</a>
        </div>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Every confirmed match</span>
        <h2 class="section-title">Everything you get on a match</h2>
        <p class="section-lead">
          Upload a name and get back the full picture on every confirmed deceased consumer.
        </p>
      </div>

      <div class="grid-4">
        @for (point of dataPoints; track point.title) {
          <article class="feature-card">
            <span class="icon-badge" [innerHTML]="iconFor(point.icon)"></span>
            <h3>{{ point.title }}</h3>
            <p>{{ point.description }}</p>
          </article>
        }
      </div>
    </div>
  </section>

  <section id="how-it-works" class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">How it works</span>
        <h2 class="section-title">From upload to verified matches in minutes</h2>
        <p class="section-lead">
          A simple weekly cycle keeps your data clean and your revenue protected.
        </p>
      </div>

      <div class="home-steps">
        @for (step of steps; track step.title; let i = $index) {
          <article class="home-step">
            <div class="home-step-top">
              <span class="home-step-num">{{ i + 1 }}</span>
              @if (i < steps.length - 1) {
                <span class="home-step-line" aria-hidden="true"></span>
              }
            </div>
            <h3>{{ step.title }}</h3>
            <p>{{ step.description }}</p>
          </article>
        }
      </div>

      <div class="text-center mt-32">
        <a class="btn btn-primary" [routerLink]="[routes.howItWorks.url]">See the full process</a>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Who we serve</span>
        <h2 class="section-title">Built for teams that pay the price of fraud</h2>
        <p class="section-lead">
          From account opening to claims and collections, VerifyDeceased fits wherever a deceased
          identity can cause loss.
        </p>
      </div>

      <div class="grid-3">
        @for (industry of industries; track industry.link) {
          <a class="feature-card industry-card" [routerLink]="[industry.link]">
            <h3>{{ industry.title }}</h3>
            <p>{{ industry.description }}</p>
            <span class="industry-link">Learn more &rarr;</span>
          </a>
        }
      </div>
    </div>
  </section>

  <!-- Security and compliance. This is the first question in any enterprise deal. -->
  <section class="section section-alt" aria-labelledby="compliance-heading">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Security and compliance</span>
        <h2 class="section-title" id="compliance-heading">Answers your risk team needs, before they ask</h2>
        <p class="section-lead">
          Encryption, retention, access control and GLBA Safeguards alignment, stated plainly. If a
          control is still in progress we say so rather than dressing it up.
        </p>
      </div>

      <ul class="attestation-row">
        @for (item of attestations; track item.name) {
          <li class="attestation" [class.is-progress]="item.state === 'progress'">
            <span class="attestation-icon" [innerHTML]="iconFor(item.icon)"></span>
            <span class="attestation-text">
              <span class="attestation-name">{{ item.name }}</span>
              <span class="attestation-status">{{ item.status }}</span>
            </span>
          </li>
        }
      </ul>

      <div class="grid-3 mt-32">
        @for (control of controlAreas; track control.title) {
          <article class="feature-card">
            <span class="icon-badge" [innerHTML]="iconFor(control.icon)"></span>
            <h3>{{ control.title }}</h3>
            <p>{{ control.description }}</p>
          </article>
        }
      </div>

      <div class="compliance-callout">
        <div>
          <h3>Running a vendor security review?</h3>
          <p>
            We complete customer questionnaires as a standard part of onboarding. Ask for the
            security package and you get our FTC Safeguards Rule control mapping, encryption and key
            management detail, the data retention schedule, incident response and breach notification
            commitments, business continuity summary, subprocessor list and our most recent
            penetration test summary.
          </p>
          <div class="actions-row">
            <a class="btn btn-primary" [routerLink]="[routes.security.url]">Read the full trust centre</a>
            <a class="btn btn-outline" [routerLink]="[routes.contact.url]">Request the security package</a>
          </div>
        </div>
        <ul class="check-list compliance-callout-list">
          <li>AES-256 at rest, TLS 1.2 or higher in transit</li>
          <li>Submitted records deleted on a defined schedule</li>
          <li>We never sell or resell the records you send us</li>
          <li>MFA on administrative and production access</li>
          <li>All data hosted in United States regions</li>
        </ul>
      </div>
    </div>
  </section>

  <section class="section section-dark">
    <div class="container">
      <div class="api-teaser">
        <div class="api-copy">
          <span class="eyebrow">For developers</span>
          <h2 class="section-title">Verify identities in real time with our API</h2>
          <p class="section-lead">
            Integrate deceased verification directly into account opening, loan origination and
            claims workflows. Send a single identity or a batch and get a confirmed result back in
            milliseconds.
          </p>
          <div class="actions-row">
            <a class="btn btn-accent" [routerLink]="[routes.developers.url]">Read the API docs</a>
            <a class="btn btn-outline btn-outline-light" [routerLink]="[routes.contact.url]">Talk to sales</a>
          </div>
        </div>
        <div class="api-code">
          <div class="api-code-head">
            <span class="api-dot"></span>
            <span class="api-dot"></span>
            <span class="api-dot"></span>
            <span class="api-code-title">POST /v1/verify</span>
          </div>
<pre>{{ apiSample }}</pre>
        </div>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="pricing-teaser">
        <div>
          <span class="eyebrow">Simple pricing</span>
          <h2 class="section-title">Pay only for verified records</h2>
          <p class="section-lead">
            No contracts and no minimums. Standard verification is $0.04 per record. Verify at least
            100,000 records per month and bulk pricing drops to $0.01 per record.
          </p>
          <p class="pricing-teaser-note">
            Opening an account costs nothing and needs no card. You add funds when you are ready to
            run a list, and unmatched records are never billed.
          </p>
        </div>
        <div class="price-cards">
          <div class="price-card">
            <span class="price-name">Standard</span>
            <span class="price-value">$0.04<small>/record</small></span>
            <span class="price-note">Pay as you go, any volume</span>
          </div>
          <div class="price-card featured">
            <span class="price-name">Bulk</span>
            <span class="price-value">$0.01<small>/record</small></span>
            <span class="price-note">100,000+ records per month</span>
          </div>
        </div>
      </div>
      <div class="text-center mt-32">
        <a class="btn btn-primary btn-lg" [routerLink]="[routes.pricing.url]">See full pricing</a>
      </div>
    </div>
  </section>

  <section class="section section-alt">
    <div class="container">
      <div class="section-head">
        <span class="eyebrow">Questions</span>
        <h2 class="section-title">Frequently asked questions</h2>
        <p class="section-lead">Everything banks, lenders and fraud teams ask before they start.</p>
      </div>

      <div class="faq-list">
        @for (faq of faqs; track faq.question) {
          <details class="faq-item">
            <summary>{{ faq.question }}</summary>
            <p>{{ faq.answer }}</p>
          </details>
        }
      </div>
      <div class="text-center mt-32">
        <a class="btn btn-outline" [routerLink]="[routes.faq.url]">View all FAQs</a>
      </div>
    </div>
  </section>

  <section class="section">
    <div class="container">
      <div class="cta-band">
        <h2>Start verifying deceased consumers today</h2>
        <p>
          Join the banks, lenders and fraud teams protecting revenue with fresh, confirmed death
          data. Creating an account is free and takes minutes. No card, no contract, no minimum, and
          you only pay $0.04 for each record we confirm as deceased.
        </p>
        <div class="actions-row center">
          <a class="btn btn-primary btn-lg" [routerLink]="[routes.accountRegister.url]">Create your free account</a>
          <a class="btn btn-outline btn-lg" [routerLink]="[routes.contact.url]">Contact sales</a>
        </div>
      </div>
    </div>
  </section>
</main>

<app-footer />

